import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormArray, Validators, FormBuilder } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Global } from 'src/app/core/resources/global';
import { Utils } from 'src/app/core/resources/utils';
import { StandardMapComponent } from 'src/app/shared/standard-map/standard-map.component';
import { MenuServiceService } from '../../menu-service/menu-service.service';
import { Router } from '@angular/router';
import { Fmt } from 'src/app/core/messages/fmt';
import { of, Subscription } from 'rxjs';
import { Patterns } from 'src/app/core/resources/patterns';
import { Destination } from 'src/app/core/interfaces/destination';
import { DateManager } from 'src/app/core/managers/date.manager';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { Address } from 'src/app/core/interfaces/address';
import { DateFormatPipe } from 'src/app/core/pipe/dateFormat.pipe';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { City } from 'src/app/core/interfaces/city';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { ThirdParty, ThirdPartyAddress } from 'src/app/core/interfaces/thirdParty';
import { ServiceResources } from '../../resources/services';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, map, take } from 'rxjs/operators';
import { GuideService } from 'src/app/modules/guides/guide.service';
import { DateEnum } from 'src/app/core/enums/date.enum';
import { GeoValidationService } from 'src/app/core/services/geo-validation.service';
import { municipalityPolygonValidator } from 'src/app/core/validators/municipality-polygon.validator';

interface DestinationForm {
  destinationCity?: City;
  destinationDate?: Date;
  isInternational?: boolean;
  destinationAddresses?: DestinationAddressForm[];
}
interface DestinationAddressForm {
  address: {
    address: string;
    location: {
      lat: number;
      lng: number;
    };
  };
  time: string;
  timeType?: string;
  cellphone?: string;
  hasThirdParty?: boolean;
  email?: string;
  guidesRecipients?: Address[];
  guidesId?: string[];
  thirdParty?: ThirdParty,
  thirdPartyAddress?: ThirdPartyAddress
}
@Component({
  selector: 'app-destination-download-service-form',
  templateUrl: './destination-download-service-form.component.html',
  styleUrls: ['./destination-download-service-form.component.scss']
})
export class DestinationDownloadServiceFormComponent implements OnInit, OnDestroy {
  @ViewChild(StandardMapComponent) standardMap!: StandardMapComponent;
  permission = Permission;
  destinationsControls = new FormArray([]);
  private mapPolygonSub: Subscription = new Subscription();
  private prevDestCodes: string[] = [];
  private destinationMarkerKeys: string[] = [];
  showDestinationMap: boolean = true;

  toggleDestinationMap(): void {
    this.showDestinationMap = !this.showDestinationMap;
  }
  initialAddresses: { initialValue: string, initialThirdParty?: string }[][] = [];
  indexOpened: { indexDestination: number, indexAddress: number } = { indexDestination: 0, indexAddress: 0 };

  readonly baseDestinationCityOptions: OptionsAutocomplete = {
    title: 'Destino (Municipio o Ciudad)',
    appearance: 'outline',
    requireFullCity: true
  };
  destinationCityOptions: OptionsAutocomplete[] = [];
  destinationAddressOptions = {
    title: 'Dirección de destino',
    appearance: 'outline',
    required: true,
    showXMark: true
  };
  formValidate = '';
  originDate: Date = new Date();
  maxDate: Date = DateManager.add(new Date(), 30, 'days');
  lastOriginHour: string = '';

  formSubscription: Subscription = new Subscription();
  isDialog: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public paramsDialog: { cargo: Cargo },
    public dialogRef: MatDialogRef<DestinationDownloadServiceFormComponent>,
    public utils: Utils,
    private fb: FormBuilder,
    private global: Global,
    public menuService: MenuServiceService,
    private router: Router,
    private patterns: Patterns,
    private snackBarService: SnackBarService,
    private dateFormatPipe: DateFormatPipe,
    private permissionRole: PermissionRole,
    private serviceResources: ServiceResources,
    private guideService: GuideService,
    private geoValidation: GeoValidationService
  ) { }

  /**
   * @description Initializes the component
   */
  async ngOnInit() {
    await this.checkDialogParams();
    this.setLocalValues();
    this.setSubscriptions();
  }

  private async checkDialogParams() {
    if (this.paramsDialog && this.paramsDialog.cargo) {
      this.isDialog = true;
      this.menuService.cargo = this.paramsDialog.cargo;
      this.menuService.patchForm(this.paramsDialog.cargo);
      await this.menuService.setCargoConsignments();
      await this.menuService.setCargoVehicle();
    }
  }

  private setLocalValues() {
    this.destinationCityOptions = [];
    this.initialAddresses = [];
    this.indexOpened = { indexDestination: 0, indexAddress: 0 };
    const dateLoad = this.utils.getNestedValue(this.menuService.form.value, 'dateLoad');
    const originAddresses = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.addresses');
    this.lastOriginHour = originAddresses && originAddresses.length ? originAddresses[originAddresses.length - 1].time : '';
    this.originDate = dateLoad ? DateManager.stringToDate(dateLoad) : new Date();
    const hasMinistry: boolean = this.utils.getNestedValue(this.menuService.form.value, 'ministry');
    this.destinationsControls.clear();
    this.addDestinationForm();
    const destinations: Destination[] = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.destination');
    if (!destinations || !destinations.length) return;

    destinations.forEach((destination: Destination, index: number) => {
      if (index) this.addDestinationForm();
      const initialDestination = [];
      const destinationControl = this.destinationsControls.at(index);
      destinationControl.get('isInternational').setValue(destination && destination.country && destination.country.code !== 'CO');
      destinationControl.get('destinationCity').setValue(destination && destination.name ? destination.name : '');
      destinationControl.get('destinationDate').setValue(DateManager.stringToDate(destination.downloadDate));
      const destinationCity = this.utils.getNestedValue(destination, 'name');

      this.destinationCityOptions[index] = {
        ...this.baseDestinationCityOptions,
        initialValue: destinationCity ? destinationCity : '',
        isInternational: !!destinationControl.get('isInternational').value
      };
      this.destinationsControls.at(index).get('destinationCity').setValue({
        name: destinationCity ? destinationCity : '',
        id: destination && destination.municipalityCode ? destination.municipalityCode : '',
        country: destination && destination.country && destination.country.name ? destination.country.name : 'Colombia',
        code: destination && destination.country && destination.country.code ? destination.country.code : 'CO',
      });
      if (destination.addresses && destination.addresses.length) {
        this.getDestinationAddresses(index).clear();
        destination.addresses.forEach((address: AddressCargo, indexAddress: number) => {
          this.addDestinationAddressForm(index);
          initialDestination.push({ initialValue: address && address.address ? address.address : '', initialThirdParty: address && address.thirdPartyConsignment && address.thirdPartyConsignment.documentNumber ? address.thirdPartyConsignment.documentNumber : '' });
          const destinationAddressControl = this.getDestinationAddresses(index).at(indexAddress);
          destinationAddressControl.get('address').setValue({
            address: address && address.address ? address.address : '',
            location: {
              lat: address && address.location && address.location.lat ? address.location.lat : 0,
              lng: address && address.location && address.location.lng ? address.location.lng : 0
            }
          });
          destinationAddressControl.get('thirdParty').setValue({
            document: address && address.thirdPartyConsignment && address.thirdPartyConsignment.documentNumber ? address.thirdPartyConsignment.documentNumber : '',
            documentType: address && address.thirdPartyConsignment && address.thirdPartyConsignment.documentType ? address.thirdPartyConsignment.documentType : '',
            name: address && address.thirdPartyConsignment && address.thirdPartyConsignment.name ? address.thirdPartyConsignment.name : ''
          });
          destinationAddressControl.get('thirdPartyAddress').setValue({
            address: {
              address: address && address.thirdPartyConsignment && address.thirdPartyConsignment.address ? address.thirdPartyConsignment.address
                : address && address.address ? address.address : '',
              location: {
                lat: address && address.location && address.location.lat ? address.location.lat : 0,
                lng: address && address.location && address.location.lng ? address.location.lng : 0
              },
              id: address && address.thirdPartyConsignment && address.thirdPartyConsignment.branchOffice ? address.thirdPartyConsignment.branchOffice : '',
            },
            branchOfficeName: address && address.address ? address.address : '',
            information: {
              document: address && address.thirdPartyConsignment && address.thirdPartyConsignment.documentNumber ? address.thirdPartyConsignment.documentNumber : '',
              name: address && address.thirdPartyConsignment && address.thirdPartyConsignment.name ? address.thirdPartyConsignment.name : ''
            }
          });
          destinationAddressControl.get('time').setValue(address && address.time ? address.time : '00:01');
          destinationAddressControl.get('cellphone').setValue(address && address.cellphone ? address.cellphone : '');
          const hasCompleteAddress = !!(address && address.address && address.location && address.location.lat && address.location.lng);
          const hasThirdParty = (this.utils.addressHasThirdParty(address) || !hasCompleteAddress) && hasMinistry && !destinationControl.get('isInternational').value;
          destinationAddressControl.get('hasThirdParty').setValue(hasThirdParty);
          destinationAddressControl.get('email').setValue(address && address.email ? address.email : '');
          this.getDestinationAddressGuidesRecipients(index, indexAddress).clear();
          if (address.guidesRecipients && address.guidesRecipients.length) {
            address.guidesRecipients.forEach((guideRecipient: Address, indexGuideRecipient: number) => {
              this.addGuideRecipientForm(index, indexAddress);
              const destinationAddressGuideRecipientControl = this.getDestinationAddressGuidesRecipients(index, indexAddress).at(indexGuideRecipient);
              destinationAddressGuideRecipientControl.get('name').setValue(guideRecipient && guideRecipient.name ? guideRecipient.name : '');
              destinationAddressGuideRecipientControl.get('email').setValue(guideRecipient && guideRecipient.email ? guideRecipient.email : '');
              destinationAddressGuideRecipientControl.get('phone').setValue(guideRecipient && guideRecipient.phone ? guideRecipient.phone : '');
              destinationAddressGuideRecipientControl.get('clientId').setValue(guideRecipient && guideRecipient.clientId ? guideRecipient.clientId : '');
            });
          }
          if (address && address.guidesId && address.guidesId.length) destinationAddressControl.get('guidesId').setValue(address.guidesId);
          //Ya llegó a la dirección en el tracking
          if (address && (address.state || (address.durationTime && address.durationTime.startDate))) {
            this.getDestinationAddresses(index).at(indexAddress).get('address').disable();
            this.getDestinationAddresses(index).at(indexAddress).get('time').disable();
          }
        });
      }
      //Ya llegó a alguna dirección en el tracking
      if (destination.addresses.some(address => !!address.state || !!(address.durationTime && address.durationTime.startDate))) {
        destinationControl.get('destinationCity').disable();
        destinationControl.get('destinationDate').disable();
        destinationControl.get('isInternational').disable();
      }
      this.initialAddresses.push(initialDestination);
    });
    this.checkPermissionsToEdit();
    this.refreshDestinationAddressesGeoValidity();
    this.refreshDestinationMarkers();
  }

  private setSubscriptions() {
    this.formSubscription = this.menuService.formObservable
      .pipe(distinctUntilChanged())
      .subscribe(() => this.setLocalValues());
    this.mapPolygonSub.add(
      this.destinationsControls.valueChanges.pipe(
        map((dests: any[]) => (dests || [])
          .map(d => d && d.isInternational ? '' : (d && d.destinationCity && d.destinationCity.id ? d.destinationCity.id : ''))
          .filter((c: string) => !!c)),
        debounceTime(50)
      ).subscribe((codes: string[]) => {
        const prev = this.prevDestCodes;
        const removed = prev.filter(c => codes.indexOf(c) === -1);
        const added = codes.filter(c => prev.indexOf(c) === -1);
        const changed = removed.length || added.length;
        this.prevDestCodes = codes.slice();
        if (changed) this.refreshDestinationAddressesGeoValidity();
        this.refreshDestinationMarkers();
        if (!this.standardMap) return;
        removed.forEach(code => this.standardMap.removeMunicipalityPolygon(`dest:${code}`));
        added.forEach(code => this.loadAndShowDestinationPolygon(code));
      })
    );
  }

  private refreshDestinationAddressesGeoValidity(): void {
    this.destinationsControls.controls.forEach((_destination, index) => {
      const addresses = this.getDestinationAddresses(index);
      addresses.controls.forEach(address => {
        const addrCtrl = address.get('address');
        const tpAddrCtrl = address.get('thirdPartyAddress');
        if (addrCtrl && addrCtrl.enabled) addrCtrl.updateValueAndValidity({ emitEvent: false });
        if (tpAddrCtrl && tpAddrCtrl.enabled) tpAddrCtrl.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  private loadAndShowDestinationPolygon(cityId: string): void {
    this.geoValidation.getMunicipalityPolygon(cityId).pipe(
      take(1),
      catchError(() => of(null))
    ).subscribe(poly => {
      if (poly && poly.geometry && this.standardMap) {
        this.standardMap.renderMunicipalityPolygon(poly.geometry, {
          key: `dest:${cityId}`,
          color: '#16a34a',
          fillOpacity: 0.1,
          fit: true
        });
      }
    });
  }

  onMapReady(): void {
    if (!this.standardMap) return;
    this.standardMap.removeMunicipalityPolygon();
    this.prevDestCodes.forEach(code => this.loadAndShowDestinationPolygon(code));
    this.refreshDestinationMarkers();
  }

  hasAnyDestinationCity(): boolean {
    return this.destinationsControls.controls.some(destination => {
      const isInternationalCtrl = destination.get('isInternational');
      if (isInternationalCtrl && isInternationalCtrl.value) return false;
      const cityCtrl = destination.get('destinationCity');
      const city: any = cityCtrl ? cityCtrl.value : null;
      return !!(city && city.id);
    });
  }

  private refreshDestinationMarkers(): void {
    if (!this.standardMap) return;
    this.destinationMarkerKeys.forEach(key => this.standardMap.removeMarker(key));
    this.destinationMarkerKeys = [];
    this.destinationsControls.controls.forEach((destination, destIndex) => {
      const isInternationalCtrl = destination.get('isInternational');
      const isInternational = !!(isInternationalCtrl && isInternationalCtrl.value);
      if (isInternational) return;
      const addresses = this.getDestinationAddresses(destIndex);
      addresses.controls.forEach((address, addrIndex) => {
        const addrCtrl = address.get('address');
        const tpCtrl = address.get('thirdPartyAddress');
        const hasTpCtrl = address.get('hasThirdParty');
        const direct: any = addrCtrl ? addrCtrl.value : null;
        const tp: any = tpCtrl ? tpCtrl.value : null;
        const usingThirdParty = !!(hasTpCtrl && hasTpCtrl.value);
        const source = usingThirdParty
          ? (tp && tp.address && tp.address.location ? tp.address.location : null)
          : (direct && direct.location ? direct.location : null);
        const lat = source && source.lat ? +source.lat : 0;
        const lng = source && source.lng ? +source.lng : 0;
        if (!lat || !lng) return;
        const key = `dest-${destIndex}-addr-${addrIndex}`;
        this.standardMap.createMarker(
          key,
          lat,
          lng,
          `Destino ${destIndex + 1} - Dirección ${addrIndex + 1}`,
          'assets/img/pin-destination.png',
          `${addrIndex + 1}`
        );
        this.destinationMarkerKeys.push(key);
      });
    });
  }

  private hasDestinationValidating(): boolean {
    for (let i = 0; i < this.destinationsControls.controls.length; i++) {
      const addresses = this.getDestinationAddresses(i);
      for (const address of addresses.controls) {
        const activeCtrl = this.getActiveDestAddressControl(address as FormGroup);
        if (activeCtrl && activeCtrl.pending) return true;
      }
    }
    return false;
  }

  /**
   * Espera a que terminen las validaciones async (polígono de municipio) que
   * estén pendientes antes de leer su resultado. Se usa polling corto porque
   * refreshDestinationAddressesGeoValidity() dispara updateValueAndValidity con
   * { emitEvent: false }, por lo que statusChanges no emite de forma fiable al
   * resolverse el validador. El timeout evita que la UI quede bloqueada si el
   * backend no responde.
   */
  private waitForDestinationValidation(timeoutMs: number = 5000, intervalMs: number = 50): Promise<void> {
    return new Promise<void>(resolve => {
      const start = Date.now();
      const check = () => {
        if (!this.hasDestinationValidating() || Date.now() - start >= timeoutMs) return resolve();
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  private hasDestinationGeoErrors(): boolean {
    for (let i = 0; i < this.destinationsControls.controls.length; i++) {
      const addresses = this.getDestinationAddresses(i);
      for (const address of addresses.controls) {
        const activeCtrl = this.getActiveDestAddressControl(address as FormGroup);
        if (activeCtrl && activeCtrl.errors && activeCtrl.errors.outsideMunicipality) return true;
      }
    }
    return false;
  }

  private checkPermissionsToEdit() {
    if (!this.canEditDestinationDownloadService) {
      this.formValidate = 'disable';
      this.destinationsControls.controls.forEach((destination, index) => {
        destination.get('destinationCity').disable();
        destination.get('destinationDate').disable();
        destination.get('isInternational').disable();
        this.getDestinationAddresses(index).controls.forEach((address, indexAddress) => {
          address.get('address').disable();
          address.get('time').disable();
          address.get('cellphone').disable();
          address.get('hasThirdParty').disable();
          address.get('email').disable();
          address.get('guidesId').disable();
          this.getDestinationAddressGuidesRecipients(index, indexAddress).controls.forEach(guideRecipient => {
            guideRecipient.get('name').disable();
            guideRecipient.get('email').disable();
            guideRecipient.get('phone').disable();
            guideRecipient.get('clientId').disable();
          });
        });
      });
    }
  }

  get canSeeDestinationDownloadService(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.showLoadDownloadModule
    );
  }

  get canEditDestinationDownloadService(): boolean {
    return this.canSeeDestinationDownloadService && this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.editLoadDownloadModule
    );
  }

  get canEditGuidesRecipients(): boolean {
    return !this.isDialog && this.menuService.isTransportService();
  }

  setIndexOpened(indexDestination: number, indexAddress: number) {
    const isSame = indexAddress === this.indexOpened.indexAddress && indexDestination === this.indexOpened.indexDestination;
    this.indexOpened = { indexDestination, indexAddress: isSame ? null : indexAddress };
  }

  addDestinationForm(setIndexOpened: boolean = false) {
    this.destinationsControls.push(this.createDestinationGroup());
    this.destinationCityOptions.push({
      ...this.baseDestinationCityOptions,
      isInternational: false
    });
    if (setIndexOpened) this.setIndexOpened(this.destinationsControls.controls.length - 1, 0);
  }

  removeDestinationForm(index: number) {
    this.destinationsControls.removeAt(index);
    this.destinationCityOptions.splice(index, 1);
  }

  getDestinationAddresses(index: number): FormArray {
    return this.destinationsControls.at(index).get('destinationAddresses') as FormArray;
  }

  canAddDestinationAddress(index: number): boolean {
    if (index + 1 >= this.destinationsControls.length) return true;
    const nextDestination = this.destinationsControls.at(index + 1) as FormGroup;
    const destinationCityControl = nextDestination.get('destinationCity');
    return destinationCityControl && !destinationCityControl.disabled;
  }

  addDestinationAddressForm(index: number, setIndexOpened: boolean = false) {
    const destinationGroup = this.destinationsControls.at(index) as FormGroup;
    const isInternationalCtrl = destinationGroup.get('isInternational');
    const isInternational = !!(isInternationalCtrl && isInternationalCtrl.value);
    this.getDestinationAddresses(index).push(this.createDestinationAddressGroup(isInternational, () => destinationGroup));
    if (setIndexOpened) this.setIndexOpened(index, this.getDestinationAddresses(index).controls.length - 1);
  }

  removeDestinationAddressForm(index: number, addressIndex: number) {
    this.getDestinationAddresses(index).removeAt(addressIndex);
    if (index === this.indexOpened.indexDestination && addressIndex === this.indexOpened.indexAddress)
      this.setIndexOpened(index, this.getDestinationAddresses(index).controls.length - 1);
  }

  getDestinationAddressGuidesRecipients(index: number, addressIndex: number): FormArray {
    return this.getDestinationAddresses(index).at(addressIndex).get('guidesRecipients') as FormArray;
  }

  addGuideRecipientForm(index: number, addressIndex: number) {
    this.getDestinationAddressGuidesRecipients(index, addressIndex).push(this.createGuideRecipientGroup());
  }

  removeGuideRecipientForm(index: number, addressIndex: number, recipientIndex: number) {
    this.getDestinationAddressGuidesRecipients(index, addressIndex).removeAt(recipientIndex);
  }

  private createDestinationGroup(): FormGroup {
    const destinationGroup = this.fb.group({
      destinationCity: ['', Validators.required],
      destinationDate: ['', Validators.required],
      isInternational: [false],
      destinationAddresses: this.fb.array([]),
    });
    const addresses = destinationGroup.get('destinationAddresses') as FormArray;
    addresses.push(this.createDestinationAddressGroup(false, () => destinationGroup));
    return destinationGroup;
  }

  private createDestinationAddressGroup(isInternational: boolean, getDestination?: () => FormGroup): FormGroup {
    const group = this.fb.group({
      address: ['', Validators.required],
      time: ['00:01', Validators.required],
      cellphone: ['', [Validators.minLength(10), Validators.pattern(this.patterns.ONLY_NUMBERS.source)]],
      hasThirdParty: [this.menuService.isMinistryService() && !isInternational],
      email: ['', [Validators.email]],
      guidesRecipients: this.fb.array([]),
      thirdParty: ['', Validators.required],
      thirdPartyAddress: ['', Validators.required],
      guidesId: ['']
    });
    if (getDestination) this.attachGeoValidators(group, getDestination);
    return group;
  }

  private attachGeoValidators(addressGroup: FormGroup, getDestination: () => FormGroup): void {
    const addressCtrl = addressGroup.get('address');
    const thirdPartyAddressCtrl = addressGroup.get('thirdPartyAddress');
    if (addressCtrl) {
      addressCtrl.setAsyncValidators(municipalityPolygonValidator(this.geoValidation, () => {
        const destination = getDestination();
        const isInternationalCtrl = destination && destination.get('isInternational');
        if (!destination || (isInternationalCtrl && isInternationalCtrl.value)) return null;
        if (!this.isDestAddressActive(addressGroup, false)) return null;
        const cityCtrl = destination.get('destinationCity');
        const city: any = cityCtrl ? cityCtrl.value : null;
        const cityId = city && city.id;
        const val: any = addressCtrl.value;
        const lat = val && val.location && val.location.lat;
        const lng = val && val.location && val.location.lng;
        if (!cityId || lat == null || lng == null) return null;
        if (+lat === 0 && +lng === 0) return null;
        return { cityId, lat: +lat, lng: +lng };
      }));
    }
    if (thirdPartyAddressCtrl) {
      thirdPartyAddressCtrl.setAsyncValidators(municipalityPolygonValidator(this.geoValidation, () => {
        const destination = getDestination();
        const isInternationalCtrl = destination && destination.get('isInternational');
        if (!destination || (isInternationalCtrl && isInternationalCtrl.value)) return null;
        if (!this.isDestAddressActive(addressGroup, true)) return null;
        const cityCtrl = destination.get('destinationCity');
        const city: any = cityCtrl ? cityCtrl.value : null;
        const cityId = city && city.id;
        const val: any = thirdPartyAddressCtrl.value;
        const lat = val && val.address && val.address.location && val.address.location.lat;
        const lng = val && val.address && val.address.location && val.address.location.lng;
        if (!cityId || lat == null || lng == null) return null;
        if (+lat === 0 && +lng === 0) return null;
        return { cityId, lat: +lat, lng: +lng };
      }));
    }
  }

  isDestAddressActive(addressGroup: FormGroup, forThirdParty: boolean): boolean {
    const hasTpCtrl = addressGroup.get('hasThirdParty');
    const hasTp = !!(hasTpCtrl && hasTpCtrl.value);
    const usingThirdParty = !this.menuService.isTransportService() || hasTp;
    return forThirdParty ? usingThirdParty : !usingThirdParty;
  }

  private getActiveDestAddressControl(addressGroup: FormGroup) {
    const usingThirdParty = this.isDestAddressActive(addressGroup, true);
    return usingThirdParty ? addressGroup.get('thirdPartyAddress') : addressGroup.get('address');
  }

  private createGuideRecipientGroup(): FormGroup {
    return this.fb.group({
      name: [''],
      email: ['', [Validators.email]],
      phone: ['', [Validators.minLength(10), Validators.pattern(this.patterns.ONLY_NUMBERS.source)]],
      clientId: ['', [
        Validators.minLength(3),
        Validators.maxLength(30),
        Validators.pattern(/^$|^[A-Za-z0-9_-]+$/)
      ]]

    });
  }

  onChangeInternational(indexDestination: number) {
    this.destinationsControls.at(indexDestination).get('destinationCity').setValue('');
    this.getDestinationAddresses(indexDestination).controls.forEach(addressDestination => {
      addressDestination.get('address').setValue('');
    });
    this.destinationCityOptions[indexDestination] = {
      ...this.baseDestinationCityOptions,
      isInternational: !!this.destinationsControls.at(indexDestination).get('isInternational').value,
      initialValue: ''
    };
  }

  getAddressInitialValue(indexDestination: number, indexAddress: number): string {
    const address = this.utils.getNestedValue(this.menuService.form.value, `cargoFeature.uploadDownload.destination.${indexDestination}.addresses.${indexAddress}.address`);
    return address ? address : '';
  }

  onSelectAddress($event: "" | { address: string, location: { lat: number, lng: number } }, indexDestination: number, indexAddress: number) {
    if ($event === "") this.getDestinationAddresses(indexDestination).at(indexAddress).get('address').setValue('');
    else this.getDestinationAddresses(indexDestination).at(indexAddress).get('address').setValue($event);
    if ($event !== "") {
      if (!this.initialAddresses[indexDestination]) this.initialAddresses[indexDestination] = [];
      if (!this.initialAddresses[indexDestination][indexAddress]) this.initialAddresses[indexDestination][indexAddress] = { initialValue: '' };
      this.initialAddresses[indexDestination][indexAddress].initialValue = $event.address;
    }
  }

  get isInternationalTrip(): boolean {
    return this.utils.getNestedValue(this.menuService.form.value, 'cargoModel.tripType.name') === 'Internacional';
  }

  backToPreviousStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.origin, this.menuService.cargo.consecutive)]);
  }

  private isValidForm(): boolean {
    for (let i = 0; i < this.destinationsControls.controls.length; i++) {
      const destination = this.destinationsControls.controls[i];
      if (this.utils.errorMessagesCustomized(destination.get('destinationCity'), `destino ${i + 1}`)) return false;
      if (this.utils.errorMessagesCustomized(destination.get('destinationDate'), `fecha del destino ${i + 1}`)) return false;
      for (let j = 0; j < this.getDestinationAddresses(i).controls.length; j++) {
        const address = this.getDestinationAddresses(i).controls[j];
        if (this.utils.errorMessagesCustomized(address.get('time'), `hora de la dirección ${j + 1} (destino ${i + 1})`)) return false;
        if (this.utils.errorMessagesCustomized(address.get('cellphone'), `celular de la dirección ${j + 1} (destino ${i + 1})`)) return false;
        if (this.utils.errorMessagesCustomized(address.get('email'), `correo de la dirección ${j + 1} (destino ${i + 1})`)) return false;
        if (this.menuService.isTransportService() && this.utils.errorMessagesCustomized(address.get('address'), `dirección ${j + 1} (destino ${i + 1})`)) return false;
        if (!this.menuService.isTransportService() && this.utils.errorMessagesCustomized(address.get('thirdParty'), `destinatario de la dirección ${j + 1} (destino ${i + 1})`)) return false;
        if (!this.menuService.isTransportService() && this.utils.errorMessagesCustomized(address.get('thirdPartyAddress'), `dirección del destinatario de la dirección ${j + 1} (destino ${i + 1})`)) return false;
      }
    }
    return true;
  }

  private isValidEstimatedTime(): { isValid: boolean, error: string } {
    let currentTime: Date = this.lastOriginHour
      ? DateManager.stringToDate(`${DateManager.setStartOfDay(this.originDate).toISOString().split('T')[0]}T${this.lastOriginHour}`, 'YYYY-MM-DDTHH:mm:ssZ')
      : this.originDate;
    for (let i = 0; i < this.destinationsControls.controls.length; i++) {
      const destinationDate = DateManager.dateToString((this.destinationsControls.controls[i] as FormGroup).getRawValue().destinationDate);
      for (let j = 0; j < this.getDestinationAddresses(i).controls.length; j++) {
        const address = this.getDestinationAddresses(i).controls[j] as FormGroup;
        const time = address.getRawValue().time;
        const timeDate = DateManager.stringToDate(`${destinationDate.substring(0, 10)} ${time} -0500`);
        if (DateManager.isSameOrBefore(timeDate, currentTime, 'minutes'))
          return {
            isValid: false, error:
              !i && !j
                ? `La hora de la dirección ${j + 1} (destino ${i + 1}) es anterior o igual a la última hora de origen`
                : `La hora de la dirección ${j + 1} (destino ${i + 1}) es anterior o igual a la dirección previa`
          };
        currentTime = timeDate;
      }
    }
    return { isValid: true, error: '' };
  }

  async saveAndContinue() {
    if (this.isDialog && !this.isValidForm()) return;
    const isValidEstimatedTime = this.isValidEstimatedTime();
    if (!isValidEstimatedTime.isValid)
      return this.snackBarService.openSnackBar(isValidEstimatedTime.error, undefined, 'alert');
    this.refreshDestinationAddressesGeoValidity();
    await this.waitForDestinationValidation();
    if (this.hasDestinationValidating())
      return this.snackBarService.openSnackBar('Espera mientras se validan las direcciones de destino.', undefined, 'alert');
    if (this.hasDestinationGeoErrors())
      return this.snackBarService.openSnackBar('Una o más direcciones están fuera del municipio de destino. Corrige las direcciones para continuar.', undefined, 'error');

    const formValues = this.utils.clone(this.destinationsControls.getRawValue());
    formValues.forEach((destination: DestinationForm, index: number) => {
      if (!this.menuService.form.get('cargoFeature.uploadDownload.destination').at(index))
        this.menuService.form.get('cargoFeature.uploadDownload.destination').push(this.menuService.createDestinationGroup());
      const formDestination = this.menuService.form.get('cargoFeature.uploadDownload.destination').at(index);
      const localDestinationCity = destination.destinationCity;
      formDestination.get('name').setValue(
        localDestinationCity && localDestinationCity.name ? localDestinationCity.name : ''
      );
      formDestination.get('municipalityCode').setValue(
        localDestinationCity && localDestinationCity.id ? localDestinationCity.id : ''
      );
      formDestination.get('country.name').setValue(
        localDestinationCity && localDestinationCity['country'] ? localDestinationCity['country'] : 'Colombia'
      );
      formDestination.get('country.code').setValue(
        localDestinationCity && localDestinationCity['code'] ? localDestinationCity['code'] : 'CO'
      );
      formDestination.get('downloadDate').setValue(DateManager.utcTransform(DateManager.setStartOfDay(destination.destinationDate)));
      const isInternationalDestination = !!(destination.isInternational);

      destination.destinationAddresses.forEach((localAddress: DestinationAddressForm, indexAddress: number) => {
        if (!this.menuService.form.get(`cargoFeature.uploadDownload.destination.${index}.addresses`).at(indexAddress))
          this.menuService.form.get(`cargoFeature.uploadDownload.destination.${index}.addresses`).push(this.menuService.createDestinationAddressGroup());
        const formAddress = this.menuService.form.get(`cargoFeature.uploadDownload.destination.${index}.addresses`).at(indexAddress);
        if (!this.menuService.isTransportService() || (localAddress.hasThirdParty && !isInternationalDestination)) {
          formAddress.get('thirdPartyConsignment.documentNumber').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.document ? localAddress.thirdParty.document : '');
          formAddress.get('thirdPartyConsignment.documentType').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.documentType ? localAddress.thirdParty.documentType : '');
          formAddress.get('thirdPartyConsignment.name').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.name ? localAddress.thirdParty.name : '');
          const documentThirdParty = this.utils.getNestedValue(localAddress, 'thirdParty.document');
          const documentThirdPartyAddress = this.utils.getNestedValue(localAddress, 'thirdPartyAddress.information.document');
          if (documentThirdParty && documentThirdPartyAddress && documentThirdParty === documentThirdPartyAddress) {
            const thirdPartyAddress = this.utils.getNestedValue(localAddress, 'thirdPartyAddress');
            formAddress.get('thirdPartyConsignment.address').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.address ? thirdPartyAddress.address.address : '');
            formAddress.get('thirdPartyConsignment.branchOffice').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.id ? thirdPartyAddress.address.id : '');
            formAddress.get('address').setValue(thirdPartyAddress && thirdPartyAddress.branchOfficeName ? thirdPartyAddress.branchOfficeName : thirdPartyAddress.address && thirdPartyAddress.address.address ? thirdPartyAddress.address.address : '');
            formAddress.get('location.lat').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.location && thirdPartyAddress.address.location.lat ? thirdPartyAddress.address.location.lat : 0);
            formAddress.get('location.lng').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.location && thirdPartyAddress.address.location.lng ? thirdPartyAddress.address.location.lng : 0);
          } else {
            formAddress.get('thirdPartyConsignment.address').setValue('');
            formAddress.get('thirdPartyConsignment.branchOffice').setValue('');
            formAddress.get('address').setValue('');
            formAddress.get('location.lat').setValue(0);
            formAddress.get('location.lng').setValue(0);
          }
        } else {
          formAddress.get('address').setValue(localAddress && localAddress.address && localAddress.address.address ? localAddress.address.address : '');
          formAddress.get('location.lat').setValue(localAddress && localAddress.address && localAddress.address.location && localAddress.address.location.lat ? localAddress.address.location.lat : 0);
          formAddress.get('location.lng').setValue(localAddress && localAddress.address && localAddress.address.location && localAddress.address.location.lng ? localAddress.address.location.lng : 0);
        }
        formAddress.get('cellphone').setValue(localAddress && localAddress.cellphone ? localAddress.cellphone : '');
        formAddress.get('email').setValue(localAddress && localAddress.email ? localAddress.email : '');
        formAddress.get('time').setValue(localAddress && localAddress.time ? localAddress.time : '00:01');
        formAddress.get('timeType').setValue('');

        formAddress.get('guidesRecipients').clear();
        const localGuidesRecipients = localAddress && localAddress.guidesRecipients ? localAddress.guidesRecipients : [];
        const localGuidesIds = localAddress && localAddress.guidesId ? localAddress.guidesId : [];
        localGuidesRecipients.forEach((localGuideRecipient: Address, indexGuideRecipient: number) => {
          formAddress.get('guidesRecipients').push(this.menuService.createGuideRecipientGroup());
          const formGuideRecipient = formAddress.get('guidesRecipients').at(indexGuideRecipient);
          formGuideRecipient.get('name').setValue(localGuideRecipient && localGuideRecipient.name ? localGuideRecipient.name : '');
          formGuideRecipient.get('email').setValue(localGuideRecipient && localGuideRecipient.email ? localGuideRecipient.email : '');
          formGuideRecipient.get('phone').setValue(localGuideRecipient && localGuideRecipient.phone ? localGuideRecipient.phone : '');
          formGuideRecipient.get('clientId').setValue(localGuideRecipient && localGuideRecipient.clientId ? localGuideRecipient.clientId : '');
        });
        formAddress.get('createGuide').setValue(!!localGuidesRecipients.length);
        formAddress.get('numberOfGuides').setValue(localGuidesRecipients.length);
        formAddress.get('guidesId').setValue(localGuidesIds);
      });
    });

    const formDestination = this.menuService.form.get(`cargoFeature.uploadDownload.destination`) as FormArray;
    for (let i = formDestination.controls.length - 1; i >= 0; i--) {
      if (i >= formValues.length) {
        formDestination.removeAt(i);
        continue;
      }
      const formDestinationAddresses = formDestination.controls[i].get('addresses') as FormArray;
      for (let j = formDestinationAddresses.controls.length - 1; j >= 0; j--) {
        if (j >= formValues[i].destinationAddresses.length) {
          formDestinationAddresses.removeAt(j);
          continue;
        }
        if (this.canEditGuidesRecipients) {
          const formDestinationGuidesRecipients = formDestinationAddresses.controls[j].get('guidesRecipients') as FormArray;
          for (let k = formDestinationGuidesRecipients.controls.length - 1; k >= 0; k--) {
            if (k >= formValues[i].destinationAddresses[j].guidesRecipients.length)
              formDestinationGuidesRecipients.removeAt(k);
          }
        }
      }
    }

    const formValue: Cargo = this.menuService.form.value;
    const hasDestinationsEnabledToCheckTripTimes = formValue.cargoFeature.uploadDownload.destination.every(destination => destination.downloadDate &&
      destination.addresses.every(address => !!(address.address && address.location.lat && address.location.lng && address.time)));
    const hasMoreThanOneDestination = formValue.cargoFeature.uploadDownload.destination.length > 1 || formValue.cargoFeature.uploadDownload.destination.some(destination => destination.addresses.length > 1);
    if (hasDestinationsEnabledToCheckTripTimes && (hasMoreThanOneDestination || this.menuService.isValidOrigin().isValid)) {
      const checkTripTimes = await this.menuService.checkTripTimes(formValue, this.menuService.isValidOrigin().isValid, true);
      if (!checkTripTimes.valid)
        return this.snackBarService.openSnackBar(checkTripTimes.error, undefined, 'error');
    }

    const { distancy, estimatedTime } = await this.menuService.getRouteGoogleData(this.menuService.form.value);
    this.menuService.form.get('distancy').setValue(distancy);
    this.menuService.form.get('estimatedTime').setValue(estimatedTime);
    if (this.menuService.form.get('routePlanId')) this.menuService.form.get('routePlanId').setValue(null);
    if (this.menuService.form.get('itineraryId')) this.menuService.form.get('itineraryId').setValue(null);
    if (this.menuService.form.get('itineraryOverviewPolylines')) this.menuService.form.get('itineraryOverviewPolylines').setValue(null);
    let guidesToUpdate = [];
    let guidesToDelete = [];
    const previousGuidesIds = this.utils.cargoCreatedGuide(this.menuService.cargo);
    const cargoId = this.menuService.cargo.id;
    if (previousGuidesIds.length && this.isDialog) {
      guidesToUpdate = this.checkGuideChange(this.menuService.cargo, this.menuService.form.value);
      guidesToDelete = this.checkGuidedelete(this.menuService.cargo, this.menuService.form.value);
      guidesToUpdate = guidesToUpdate.filter(guide => !guidesToDelete.includes(guide.guideId));
    }
    this.menuService.updateService().then(async () => {
      if (guidesToUpdate.length) await this.updateGuides(guidesToUpdate, cargoId);
      if (guidesToDelete.length) await this.deleteGuides(guidesToDelete, cargoId);
      this.snackBarService.openSnackBar("Servicio actualizado exitosamente");
      if (!this.isDialog) this.continueToNextStep();
      else this.dialogRef.close({ state: true });
    }).catch(() => this.snackBarService.openSnackBar('Ocurrió un error al actualizar el servicio', undefined, 'error'));
  }

  continueToNextStep() {
    if (this.menuService.isSatelliteLockService())
      return this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.escortedVehicle, this.menuService.cargo.consecutive)]);
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.vehicle, this.menuService.cargo.consecutive)]);
  }

  private buildGuidesMap(destinations: Destination[]) {
    const guidesMap = {};
    destinations.forEach((destination, indexDestination) => {
      destination.addresses.forEach((address, indexAddress) => {
        if (!address.guidesId || !address.guidesId.length) return;
        address.guidesId.forEach(guideId => {
          guidesMap[guideId] = {
            destinationIndex: indexDestination,
            addressIndex: indexAddress,
            address: address.address,
            location: address.location,
            city: destination.name,
            date: destination.downloadDate,
          };
        });
      });
    });
    return guidesMap;
  }

  private checkGuideChange(previousCargo: Cargo, newCargo: Cargo) {
    let guidesToUpdate = [];
    let previousGuides = {};
    let newGuides = {};
    const previousDestinations: Destination[] = this.utils.getNestedValue(previousCargo, 'cargoFeature.uploadDownload.destination');
    const newDestinations: Destination[] = this.utils.getNestedValue(newCargo, 'cargoFeature.uploadDownload.destination');
    previousGuides = this.buildGuidesMap(previousDestinations);
    newGuides = this.buildGuidesMap(newDestinations);
    const fieldsToCheck = [{
      guideField: 'address',
      guideCargoField: 'recipient.address',
    },
    {
      guideField: 'location',
      guideCargoField: 'recipient.location',
    },
    {
      guideField: 'city',
      guideCargoField: 'recipient.city',
    },
    {
      guideField: 'date',
      guideCargoField: 'deadline',
    },
    {
      guideField: 'destinationIndex',
      guideCargoField: 'recipient.destinationId',
    },
    {
      guideField: 'addressIndex',
      guideCargoField: 'recipient.addressId',
    }
    ]
    Object.keys(newGuides).forEach(guideId => {
      if (!previousGuides[guideId]) return;
      let guideChanges = {};
      fieldsToCheck.forEach(field => {
        const previousValue = this.utils.getNestedValue(previousGuides[guideId], field.guideField);
        const newValue = this.utils.getNestedValue(newGuides[guideId], field.guideField);
        if (this.utils.isDefined(previousValue) && this.utils.isDefined(newValue) && !this.utils.deepEqual(previousValue, newValue)) {
          if (field.guideCargoField.includes('.')) {
            const fieldParts = field.guideCargoField.split('.');
            if (!guideChanges[fieldParts[0]]) guideChanges[fieldParts[0]] = {};
            guideChanges[fieldParts[0]][fieldParts[1]] = newValue;
          } else if (field.guideCargoField === 'deadline') guideChanges[field.guideCargoField] = DateManager.formatDate(newValue, DateEnum.YYYY_MM_DD_HH_mm_ZZ, 'YYYY-MM-DD');
          else guideChanges[field.guideCargoField] = newValue;
        }
      });
      if (Object.keys(guideChanges).length) {
        guideChanges['guideId'] = guideId;
        guidesToUpdate.push(guideChanges);
      }
    });
    return guidesToUpdate;
  }

  private checkGuidedelete(previousCargo: Cargo, newCargo: Cargo) {
    const previousGuides = this.utils.cargoCreatedGuide(previousCargo);
    const newGuides = this.utils.cargoCreatedGuide(newCargo);
    const guidesToDelete = previousGuides.filter(guide => !newGuides.includes(guide));
    return guidesToDelete;
  }

  private async updateGuides(guideChanges: any, cargoId: string) {
    try {
      await this.guideService.updateGuides(guideChanges, cargoId).toPromise();
    } catch (error) {
      console.error(error);
      return this.snackBarService.openSnackBar('Ocurrió un error al actualizar las guías', undefined, 'error');
    }
    return true;
  }

  private async deleteGuides(guidesIds: string[], cargoId: string) {
    try {
      await this.guideService.changeGuideStatusDelete(guidesIds, cargoId).toPromise();
    } catch (error) {
      console.error(error);
      return this.snackBarService.openSnackBar('Ocurrió un error al eliminar las guías', undefined, 'error');
    }
    return true;
  }
  ngOnDestroy() {
    if (this.formSubscription) this.formSubscription.unsubscribe();
    if (this.mapPolygonSub) this.mapPolygonSub.unsubscribe();
    if (this.standardMap) this.standardMap.removeMunicipalityPolygon();
  }
}
